import React from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { CircularProgress } from '@material-ui/core'
import axios from 'axios'
import { toast } from 'react-toastify'
import BidEstimationHeader from '../shared/BidEstimationHeader'
import MaterialComparisonCard from './MaterialComparisonCard'
import CatalogLinkDialog from './CatalogLinkDialog'
import { SourceChip } from './SourceChip'

const CATEGORIES = ['All', 'Aggregate', 'Irrigation', 'Plant', 'Lighting', 'Hardscape', 'General']

const fmt$ = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
const fmt$0 = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const fmtQty = (v) => Number(v || 0).toLocaleString('en-US')

function StatsStrip ({ materials }) {
  const totalCost = materials.reduce((s, m) => s + (m.extendedCost || 0), 0)
  const needsDecision = materials.filter(m => m.status === 'needs_decision').length
  const vendorSelected = materials.filter(m => m.status === 'vendor_selected' || m.status === 'single_source').length
  const unmatched = materials.filter(m => m.status === 'unmatched').length

  return (
    <div className="mat-stats">
      <Stat label="Total Materials Cost" value={fmt$0(totalCost)} caption={`${materials.length} line items`} />
      <Stat label="Needs Decision" value={String(needsDecision)} caption="multi-vendor materials" tone={needsDecision ? 'warn' : null} />
      <Stat label="Vendor Selected" value={String(vendorSelected)} caption="ready for ordering" tone={vendorSelected ? 'ok' : null} />
      <Stat label="Unmatched" value={String(unmatched)} caption="need catalog link" tone={unmatched ? 'risk' : null} />
    </div>
  )
}

function Stat ({ label, value, caption, tone }) {
  return (
    <div className="mat-stat">
      <span className="eyebrow">{label}</span>
      <span className={`num${tone ? ' tone-' + tone : ''}`}>{value}</span>
      <span className="sub">{caption}</span>
    </div>
  )
}

function Section ({ tone, icon, title, count, note, defaultOpen = true, actions, children }) {
  const [open, setOpen] = React.useState(defaultOpen)
  if (count === 0) return null
  return (
    <section className="mat-section">
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button className="mat-sec-head" style={{ flex: 1 }} onClick={() => setOpen(!open)}>
          <span className={`mat-sec-icon mat-sec-icon--${tone}`}>{icon}</span>
          <span className="mat-sec-title">{title}</span>
          <span className="mat-sec-count">{count}</span>
          <span className="mat-sec-note">{note}</span>
          <span className="mat-sec-chev">{open ? '▾' : '▸'}</span>
        </button>
        {actions && open && <div style={{ display: 'flex', gap: 6, padding: '0 12px', flexShrink: 0 }}>{actions}</div>}
      </div>
      {open && <div className="mat-sec-body">{children}</div>}
    </section>
  )
}

function MatchedRow ({ m }) {
  const opt = m.vendorOptions?.[0]
  const catalogItem = m.materialCatalogItemId && typeof m.materialCatalogItemId === 'object' ? m.materialCatalogItemId : null
  const catalogName = catalogItem?.canonicalName || m.canonicalName
  const hasAspireLink = catalogItem?.aspireItemIds?.length > 0
  return (
    <div className="mat-row mat-row--matched">
      <SourceClusterFromMaterial m={m} />
      <div className="mat-row-main">
        <div className="mat-row-title">
          <span className="mat-row-name">{m.canonicalName}</span>
          <span className="mat-row-meta">{m.category} · {fmtQty(m.totalQty)} {m.unit}</span>
        </div>
        {catalogItem && (
          <div className="mat-row-sub">
            <span>Linked to <b>{catalogName}</b></span>
            {hasAspireLink && <SourceChip kind="aspire" size="xs" />}
            {opt?.vendorSku && <><span className="dot-sep">·</span><span className="mono" style={{ fontFamily: 'var(--f-mono)', fontSize: 11 }}>{opt.vendorSku}</span></>}
          </div>
        )}
      </div>
      <div className="mat-row-right">
        {opt && <span className="mat-row-price">{fmt$(opt.unitPrice)}</span>}
        {opt && <span className="mat-row-uom">/ {opt.uom || m.unit}</span>}
        <span className="mat-pill mat-pill--ok"><span className="mat-pill-dot" />{m.status === 'vendor_selected' ? 'Selected' : 'Matched'}</span>
      </div>
    </div>
  )
}

function UnmatchedRow ({ m, onLink, onSkip }) {
  const opt = m.vendorOptions?.[0]
  return (
    <div className="mat-row mat-row--unmatched">
      <SourceClusterFromMaterial m={m} />
      <div className="mat-row-main">
        <div className="mat-row-title">
          <span className="mat-row-name">{m.canonicalName}</span>
          <span className="mat-row-meta">{m.category} · {fmtQty(m.totalQty)} {m.unit}</span>
        </div>
        {opt && (
          <div className="mat-row-sub">
            <span>from <b>{opt.vendor}</b></span>
            {opt.vendorSku && <><span className="dot-sep">·</span><span className="mono" style={{ fontFamily: 'var(--f-mono)', fontSize: 11 }}>{opt.vendorSku}</span></>}
            {opt.unitPrice != null && <><span className="dot-sep">·</span><span className="mono" style={{ fontFamily: 'var(--f-mono)', fontSize: 11 }}>{fmt$(opt.unitPrice)}/{opt.uom || m.unit}</span></>}
          </div>
        )}
      </div>
      <div className="mat-row-right">
        <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => onSkip(m._id)}>Skip</button>
        <button className="hmi-btn hmi-btn-primary hmi-btn-sm" onClick={() => onLink(m)}>Link to catalog →</button>
      </div>
    </div>
  )
}

function SourceClusterFromMaterial ({ m }) {
  const sources = React.useMemo(() => {
    const s = []
    if (m.vendorOptions?.length > 0) {
      const vendors = [...new Set(m.vendorOptions.map(o => o.vendor).filter(Boolean))]
      vendors.forEach(v => s.push({ kind: 'quote', label: v }))
    }
    if (s.length === 0) s.push({ kind: 'plans', label: 'Plans' })
    return s
  }, [m.vendorOptions])

  return (
    <div className="src-cluster">
      {sources.map((src, i) => <SourceChip key={i} kind={src.kind} label={src.label} />)}
    </div>
  )
}

const MaterialsTab = ({ url }) => {
  const { projectId } = useParams()
  const [materials, setMaterials] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)
  const [autoMatching, setAutoMatching] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [categoryFilter, setCategoryFilter] = React.useState('All')
  const [linkTarget, setLinkTarget] = React.useState(null)

  const fetchMaterials = () => {
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/materials`)
      .then(res => { setMaterials(res.data); setLoading(false) })
      .catch(() => setLoading(false))
  }

  React.useEffect(() => { fetchMaterials() }, [projectId])

  const handleRefresh = () => {
    setRefreshing(true)
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/materials/refresh`)
      .then(res => { setMaterials(res.data); setRefreshing(false) })
      .catch(() => setRefreshing(false))
  }

  const handleSelectVendor = (materialId, vendorQuoteId, vendorLineIdx) => {
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${materialId}/select-vendor`, { vendorQuoteId, vendorLineIdx })
      .then(() => {
        fetchMaterials()
        toast.success('Vendor selected.')
      })
      .catch(err => console.error('Select vendor failed:', err))
  }

  const handleSkip = (materialId) => {
    const name = materials.find(m => m._id === materialId)?.canonicalName
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${materialId}/skip`)
      .then(() => {
        fetchMaterials()
        toast.info(`Skipped ${name || 'material'}.`)
      })
      .catch(err => console.error('Skip failed:', err))
  }

  const handleAutoMatch = () => {
    setAutoMatching(true)
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/materials/auto-match`)
      .then(res => {
        setMaterials(res.data.materials)
        setAutoMatching(false)
        if (res.data.linked > 0) {
          toast.success(`Auto-matched ${res.data.linked} of ${res.data.total} unmatched materials.`)
        } else {
          toast.info(`No auto-matches found — ${res.data.total} materials need manual linking.`)
        }
      })
      .catch(() => { setAutoMatching(false); toast.error('Auto-match failed.') })
  }

  const handleBulkSkip = () => {
    const count = materials.filter(m => m.status === 'unmatched').length
    if (!count) return
    if (!window.confirm(`Skip all ${count} unmatched materials?`)) return
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/materials/bulk-skip`)
      .then(res => {
        setMaterials(res.data.materials)
        toast.info(`Skipped ${res.data.skipped} unmatched materials.`)
      })
      .catch(() => toast.error('Bulk skip failed.'))
  }

  const filtered = React.useMemo(() => {
    let list = materials
    if (categoryFilter !== 'All') list = list.filter(m => (m.category || '').toLowerCase() === categoryFilter.toLowerCase())
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(m =>
        (m.canonicalName || '').toLowerCase().includes(q) ||
        m.vendorOptions?.some(o => (o.vendor || '').toLowerCase().includes(q) || (o.vendorSku || '').toLowerCase().includes(q))
      )
    }
    return list
  }, [materials, categoryFilter, search])

  const needsDecision = filtered.filter(m => m.status === 'needs_decision' || (m.status === 'vendor_selected' && m.vendorOptions?.length > 1))
  const matched = filtered.filter(m => m.status === 'single_source' || (m.status === 'vendor_selected' && m.vendorOptions?.length <= 1))
  const unmatched = filtered.filter(m => m.status === 'unmatched')

  if (loading) {
    return (
      <div className="hmi-page materials-tab" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}>
        <BidEstimationHeader breadcrumbs={[{ label: 'Projects', to: '/bid-estimation' }, { label: 'Materials' }]} title="Materials" />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
      </div>
    )
  }

  return (
    <div className="hmi-page materials-tab" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}>
      <BidEstimationHeader
        breadcrumbs={[{ label: 'Projects', to: '/bid-estimation' }, { label: 'Project', to: `/bid-estimation/${projectId}` }, { label: 'Materials' }]}
        title="Materials"
      />
      <div className="hmi-body">
        <StatsStrip materials={materials} />

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', width: 280 }}>
            <input
              type="text"
              placeholder="Search materials, vendors, SKUs..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', height: 34, padding: '0 12px', borderRadius: 8,
                border: '1px solid var(--line-2)', background: 'var(--bg-panel-alt)',
                fontSize: 13, outline: 'none', fontFamily: 'var(--f-sans, var(--font-ui))'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 2 }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`hmi-btn hmi-btn-sm ${categoryFilter === cat ? '' : 'hmi-btn-ghost'}`}
                style={categoryFilter === cat ? { background: 'var(--brand, var(--primary))', color: '#fff', fontWeight: 600, borderColor: 'transparent' } : { fontWeight: 500 }}
                onClick={() => setCategoryFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button className="hmi-btn hmi-btn-outline" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? 'Refreshing...' : '↻ Refresh'}
            </button>
          </div>
        </div>

        {/* Sections */}
        <Section tone="warn" icon="◆" title="Needs Decision" count={needsDecision.length}
          note="Multiple vendors quoted the same thing — pick which one to buy from."
          actions={needsDecision.filter(m => m.status === 'needs_decision').length > 1 && (
            <button className="hmi-btn hmi-btn-outline hmi-btn-sm" onClick={() => {
              needsDecision.filter(m => m.status === 'needs_decision').forEach(m => {
                const prices = m.vendorOptions.filter(o => o.unitPrice > 0)
                if (prices.length === 0) return
                const best = prices.reduce((a, b) => b.unitPrice < a.unitPrice ? b : a)
                handleSelectVendor(m._id, best.vendorQuoteId, best.vendorQuoteLineIdx)
              })
            }}>Select all cheapest</button>
          )}>
          {needsDecision.map(m => (
            <MaterialComparisonCard key={m._id} material={m} onSelectVendor={handleSelectVendor} />
          ))}
        </Section>

        <Section tone="risk" icon="⚠" title="Unmatched" count={unmatched.length}
          note="No catalog link yet. Link each to an Aspire item, our material catalog, or a vendor product."
          actions={unmatched.length > 0 && (
            <>
              <button className="hmi-btn hmi-btn-primary hmi-btn-sm" onClick={() => setLinkTarget(unmatched[0])}>
                Link all →
              </button>
              <button className="hmi-btn hmi-btn-outline hmi-btn-sm" onClick={handleAutoMatch} disabled={autoMatching}>
                {autoMatching ? 'Matching...' : '⚡ Auto-match'}
              </button>
              <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={handleBulkSkip}>Skip all</button>
            </>
          )}>
          {unmatched.map(m => (
            <UnmatchedRow key={m._id} m={m} onLink={setLinkTarget} onSkip={handleSkip} />
          ))}
        </Section>

        <Section tone="ok" icon="✓" title="Matched" count={matched.length}
          note="Linked to a catalog item — ready for ordering." defaultOpen={matched.length <= 20}>
          {matched.map(m => (
            <MatchedRow key={m._id} m={m} />
          ))}
        </Section>

        {materials.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
            No materials yet. Upload and match vendor quotes to populate this tab.
          </div>
        )}
      </div>

      {linkTarget && (
        <CatalogLinkDialog
          material={linkTarget}
          materialId={linkTarget._id}
          projectId={projectId}
          url={url}
          queue={unmatched}
          queueIndex={unmatched.findIndex(m => m._id === linkTarget._id)}
          onSkipMaterial={handleSkip}
          onLinked={() => {
            setLinkTarget(null)
            fetchMaterials()
            toast.success('Material linked successfully.')
          }}
          onClose={() => {
            setLinkTarget(null)
            fetchMaterials()
          }}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(MaterialsTab)
